import React, { ReactElement, useEffect } from 'react';
import {
  Box,
  Tab,
  tabClasses,
  Tabs,
  Typography,
  Stack,
  IconButton,
  Container,
  Paper,
} from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { styled } from '@mui/styles';
import { useAuth } from '../../auth/auth-hook';
import { CustomHeader } from '../../../shared/components/layout/CustomHeader';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { PROJECT_COLOR } from '../../../shared/constants';

const ROUTES = [
  {
    path: '/users/:id',
    name: 'Detalles del usuario',
  },
  {
    path: '/users/:id/subscriptions',
    name: 'Suscripciones',
  },
];

type SettingLayoutProps = {
  children: ReactElement;
};

export const UserDetailsLayout: React.FC<SettingLayoutProps> = ({ children }) => {
  const [currentTab, setCurrentTab] = React.useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const currentRoute = ROUTES.find((route) => route.path === location.pathname);
  const { user } = useAuth();
  const lastSegment = location.pathname.split('/').pop();

  const TabItem = styled(Tab)({
    position: 'relative',
    borderRadius: '30px !important',
    textAlign: 'center',
    transition: 'all .5s',
    padding: '8px 10px !important',
    color: '#BBBBBB',
    height: '35px !important',
    margin: '6px 2px',
    float: 'none',
    lineHeight: '14px',
    fontSize: '14px',
    fontWeight: '700',
    minHeight: '38px',
    textTransform: 'capitalize',
    [`&.${tabClasses.selected}, &:hover`]: {
      color: PROJECT_COLOR ?? '#000000',
      backgroundColor: (PROJECT_COLOR ?? '#000000') + '20 !important',
    },
  });

  useEffect(() => {
    ROUTES.forEach((route, i) => {
      const path = route.path.replace(':id', id as string);
      if (path === location.pathname) {
        setCurrentTab(i + 1);
      }
    });
  }, [location.pathname, id]);

  const handleChange = (event: React.SyntheticEvent | null, newValue: number | null): void => {
    setCurrentTab(newValue ?? 0);
  };

  return (
    <>
      <CustomHeader
        title={`Bienvenido de vuelta, ${user?.name}`}
        subtitle={
          'Mide el IMPACTO de tus publicitaciones, registros, choques y tráfico del sitio web.'
        }
      />
      <Container
        maxWidth={'lg'}
        style={{
          borderRadius: '16px',
          padding: '16px 16px',
          background: 'white',
        }}
      >
        <Stack direction="column" width={'100%'} spacing={2}>
          <Box
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              paddingBottom: 1,
            }}
          >
            <Tabs
              sx={{
                padding: { sm: '0px 10px !important' },
                display: 'flex',
                justifyContent: 'space-between',
                '& .MuiTabs-indicator': {
                  display: 'none !important',
                },
                width: '100%',
              }}
              value={currentTab}
              onChange={handleChange}
              aria-label="scrollable auto tabs example"
              variant="scrollable"
              allowScrollButtonsMobile
            >
              <IconButton
                onClick={() => navigate('/users')}
                sx={{ width: '30px', height: '30px', my: '10px' }}
              >
                <ArrowBackIosIcon sx={{ fontSize: 14 }} />
              </IconButton>

              {ROUTES.map((route, key) => {
                const path = route.path.replace(':id', id as string);

                return <TabItem key={key} label={route.name} onClick={() => navigate(path)} />;
              })}

              {lastSegment === 'history' && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    marginLeft: 'auto',
                  }}
                >
                  <IconButton
                    // onClick={() => navigate('/filter')}
                    sx={{ width: '30px', height: '30px', my: '10px' }}
                  >
                    <FilterAltOutlinedIcon sx={{ fontSize: 24 }} />
                  </IconButton>
                </Box>
              )}
            </Tabs>
          </Box>
          <Paper variant="outlined">
            <Box width="100%" py={'16px'}>
              {currentRoute?.name !== 'Usuarios' && currentRoute?.name !== 'Invitaciones' ? (
                <Box>
                  <Typography
                    sx={{ textAlign: { xs: 'center', md: 'start' } }}
                    fontSize={{ xs: '18px', md: '22px' }}
                    fontWeight={700}
                  >
                    {currentRoute?.name}
                  </Typography>
                </Box>
              ) : null}
              <Box>{children}</Box>
            </Box>
          </Paper>
        </Stack>
      </Container>
    </>
  );
};
